import { orderApi } from 'components/order/orderApi';
import { isDefined } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { LocationRouteParams } from 'pages/routes';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { ApplicationState } from 'store/store';

export function useGiftCards(defaultGiftcard?: string) {
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { locationId } = useParams<LocationRouteParams>();
    const [giftCard, setGiftCard] = React.useState<string | undefined>(defaultGiftcard);
    const [giftCardBalance, setGiftCardBalance] = React.useState<number>();
    const [isPartiallyRedeemable, setIsPartiallyRedeemable] = React.useState<boolean>();
    const [giftCardFreshlyApplied, setGiftCardFreshlyApplied] = React.useState(false);
    const [giftCardError, setGiftCardError] = React.useState<string>();
    const [giftCardLoading, setGiftCardLoading] = React.useState(false);
    const { user } = useAuth();
    const resetGiftCard = React.useCallback(() => {
        setGiftCard(undefined);
        setGiftCardError(undefined);
        setGiftCardBalance(undefined);
        setIsPartiallyRedeemable(undefined);
        setGiftCardFreshlyApplied(false);
    }, []);
    const submitGiftCard = React.useCallback(
        async (value?: string) => {
            setGiftCardError(undefined);
            if (!isDefined(value)) {
                setGiftCard(undefined);
                setGiftCardBalance(undefined);
                setIsPartiallyRedeemable(undefined);
                setGiftCardFreshlyApplied(false);
            } else {
                setGiftCardLoading(true);
                try {
                    const result = await orderApi.getGiftcard(
                        value,
                        settings?.giftCardPaymentProvider ?? '',
                        locationId ?? ''
                    );
                    setGiftCardBalance(result.balance);
                    setIsPartiallyRedeemable(result.isPartiallyRedeemable);
                    setGiftCard(value);
                    setGiftCardFreshlyApplied(true);
                } catch (e: any) {
                    setGiftCardError(e.data.message);
                    setGiftCard(undefined);
                } finally {
                    setGiftCardLoading(false);
                }
            }
        },
        [locationId, settings?.giftCardPaymentProvider]
    );
    React.useEffect(() => {
        const checkGiftCard = async () => {
            if (giftCard && user && !isDefined(giftCardBalance)) {
                setGiftCardLoading(true);
                try {
                    const result = await orderApi.getGiftcard(
                        giftCard,
                        settings?.giftCardPaymentProvider ?? '',
                        locationId ?? ''
                    );
                    setGiftCardBalance(result.balance);
                    setIsPartiallyRedeemable(result.isPartiallyRedeemable);
                } catch {
                    setGiftCard(undefined);
                } finally {
                    setGiftCardLoading(false);
                }
            }
        };
        checkGiftCard();
    }, [giftCard, giftCardBalance, locationId, settings?.giftCardPaymentProvider, user]);
    const resetGiftCardErrors = React.useCallback(() => {
        setGiftCardError(undefined);
    }, []);
    const handleConfirmOverpayment = React.useCallback(() => {
        setGiftCardFreshlyApplied(false);
    }, []);
    return {
        giftCard,
        giftCardBalance,
        giftCardError,
        resetGiftCard,
        submitGiftCard,
        resetGiftCardErrors,
        confirmGiftcardOverpayment: handleConfirmOverpayment,
        giftCardLoading,
        gifcardPartiallyRedeemable: isPartiallyRedeemable,
        giftCardFreshlyApplied
    };
}
